/**
 * ImportConfigForm Component
 *
 * Form for creating or editing import configuration.
 * Includes spreadsheet connection fields, test connection, and account mappings.
 */

import { useState, useCallback } from 'react';

import type {
  ImportConfig,
  ImportConfigFormData,
  TestConnectionResult,
} from '@/types/accounting';

import { TestConnectionButton } from './TestConnectionButton';

interface ImportConfigFormProps {
  initialConfig?: ImportConfig;
  onSubmit: () => void;
  onCancel: () => void;
  onTestConnection: (spreadsheetId: string, sheetName: string) => Promise<TestConnectionResult>;
  createConfig: (data: ImportConfigFormData) => Promise<ImportConfig>;
  updateConfig: (data: Partial<ImportConfigFormData>) => Promise<ImportConfig>;
}

export function ImportConfigForm({
  initialConfig,
  onSubmit,
  onCancel,
  onTestConnection,
  createConfig,
  updateConfig,
}: ImportConfigFormProps) {
  const isEditing = initialConfig?.configured ?? false;

  // Form state
  const [spreadsheetId, setSpreadsheetId] = useState(initialConfig?.spreadsheetId || '');
  const [sheetName, setSheetName] = useState(initialConfig?.sheetName || '');

  // Test connection state
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  // Submit state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // Handle test connection
  const handleTestConnection = useCallback(async () => {
    if (!spreadsheetId.trim() || !sheetName.trim()) {
      setErrors(['Please enter both Spreadsheet ID and Sheet Name']);
      return;
    }

    setIsTesting(true);
    setErrors([]);

    try {
      const result = await onTestConnection(spreadsheetId.trim(), sheetName.trim());
      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to test connection',
      });
    } finally {
      setIsTesting(false);
    }
  }, [spreadsheetId, sheetName, onTestConnection]);

  // Handle form submit
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!spreadsheetId.trim() || !sheetName.trim()) {
        setErrors(['Please enter both Spreadsheet ID and Sheet Name']);
        return;
      }

      setIsSubmitting(true);
      setErrors([]);

      try {
        const formData: ImportConfigFormData = {
          spreadsheetId: spreadsheetId.trim(),
          sheetName: sheetName.trim(),
        };

        if (isEditing) {
          await updateConfig(formData);
        } else {
          await createConfig(formData);
        }

        onSubmit();
      } catch (error) {
        if (error instanceof Error) {
          setErrors([error.message]);
        } else {
          setErrors(['Failed to save configuration']);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [spreadsheetId, sheetName, isEditing, createConfig, updateConfig, onSubmit]
  );


  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Spreadsheet Connection Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">1. Connect Spreadsheet</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Enter the Google Sheets ID and sheet name where your bank syncs transactions.
          </p>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Spreadsheet ID */}
          <div>
            <label htmlFor="spreadsheetId" className="block text-sm font-medium text-gray-700 mb-1.5">
              Spreadsheet ID
            </label>
            <input
              type="text"
              id="spreadsheetId"
              value={spreadsheetId}
              onChange={(e) => setSpreadsheetId(e.target.value)}
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1.5">
              Find this in your Google Sheets URL: docs.google.com/spreadsheets/d/<strong>[SPREADSHEET_ID]</strong>/edit
            </p>
          </div>

          {/* Sheet Name */}
          <div>
            <label htmlFor="sheetName" className="block text-sm font-medium text-gray-700 mb-1.5">
              Sheet Name
            </label>
            <input
              type="text"
              id="sheetName"
              value={sheetName}
              onChange={(e) => setSheetName(e.target.value)}
              placeholder="Transactions"
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1.5">
              The tab name at the bottom of your spreadsheet
            </p>
          </div>

          {/* Test Connection Button */}
          <TestConnectionButton
            onTest={handleTestConnection}
            isLoading={isTesting}
            result={testResult}
            disabled={!spreadsheetId.trim() || !sheetName.trim()}
          />
        </div>
      </div>

      {/* Error Display */}
      {errors.length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Form Actions */}
      <div className="bg-white rounded-xl border border-gray-200 px-6 py-4 flex justify-between items-center">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !spreadsheetId.trim() || !sheetName.trim()}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Saving...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              {isEditing ? 'Save Changes' : 'Save & Start Import'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}
